import React, { useState, useEffect, useContext } from 'react';
import { getProviderRequest } from '../../../hooks/api/odoo';
import { keyUserActiveEnterpriseId } from '../../../assets/js/SessionStorageNames';
import { GlobalContext } from '../../../contexts/GlobalContext';
import { useProviders } from '../../../hooks/api/clientsProvides';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle, faPaperPlane, faBox, faCalendarAlt, faUser, faHourglassHalf, faClipboardCheck } from '@fortawesome/free-solid-svg-icons';
import { AdditionalImagesSliderModal } from '../AdditionalImagesSliderModal';
import ProductCardWidget from './ProductCardWidget';
import ProductModalWidget from './ProductModalWidget';
import TabsWidget from './TabsWidget';
import PaginationWidget from './PaginationWidget';
import { cancelProviderRequest } from '../../../hooks/api/odoo';

// Función para marcar un producto como revisado
const markProductAsReviewed = async (productId) => {
  try {
    // Obtener productos revisados del localStorage
    const reviewedProducts = JSON.parse(localStorage.getItem('reviewedProducts') || '[]');
    
    // Agregar el producto a la lista si no existe
    if (!reviewedProducts.includes(productId)) {
      reviewedProducts.push(productId);
      localStorage.setItem('reviewedProducts', JSON.stringify(reviewedProducts));
    }
    
    console.log('Producto marcado como revisado:', productId);
    console.log('Productos revisados actuales:', reviewedProducts);
    
    return { success: true, message: 'Producto marcado como revisado' };
  } catch (error) {
    console.error('Error al marcar como revisado:', error);
    return { success: false, message: 'Error al marcar como revisado' };
  }
};

const TABS = [
  { key: 'PENDING', label: 'Pendientes', icon: faHourglassHalf },
  { key: 'PROCESSING', label: 'En proceso', icon: faClipboardCheck },
  { key: 'APPROVED', label: 'Aprobados', icon: faCheckCircle },
  { key: 'REJECTED', label: 'Rechazados', icon: faTimesCircle },
  { key: 'CANCELLED', label: 'Cancelados', icon: faTimesCircle },
];

const STATUS_LABELS = {
  pending: 'Pendiente',
  processing: 'En proceso',
  approved: 'Aprobado',
  rejected: 'Rechazado',
};

const ProductsSentsClient = () => {
  const globalContext = useContext(GlobalContext);
  const { providersDashboardData } = useProviders();
  const [products, setProducts] = useState([]);
  const [selectedTab, setSelectedTab] = useState('PENDING');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  // Paginación
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalItems: 0, itemsPerPage: 5, hasNextPage: false, hasPrevPage: false });
  // Modal de detalles
  const [selectedProduct, setSelectedProduct] = useState(null);
  // Estado para slider de imágenes adicionales
  const [sliderOpen, setSliderOpen] = useState(false);
  const [sliderImages, setSliderImages] = useState([]);
  // Estado para popup de cancelar
  const [showCancelPopup, setShowCancelPopup] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [selectedCancelProduct, setSelectedCancelProduct] = useState(null);
  const [cancelError, setCancelError] = useState('');

  // Función para convertir base64 puro a data URL para mostrar imágenes
  const convertBase64ToDataUrl = (base64String) => {
    if (!base64String) return null;
    if (base64String.startsWith('data:')) {
      return base64String;
    }
    return `data:image/png;base64,${base64String}`;
  };

  // Fetch productos según tab
  const fetchProducts = async (tabKey, pageNumber = page, pageItemCount = itemsPerPage) => {
    setLoading(true);
    setError(null);
    try {
      // Cablear el entId
      const entId = '5f1378dcef2f752aeda16673';
      // Lista de estatus válidos
      const validStatuses = ['PENDING', 'PROCESSING', 'APPROVED', 'REJECTED', 'FAILED', 'CANCELLED'];
      let status = '';
      // Mapear el tabKey a los estatus válidos
      if (validStatuses.includes(tabKey)) {
        status = tabKey;
      } 
      // Para enviados, traemos todos y filtramos por saliente > 0
      const ownerId = providersDashboardData?.ownerId;
      const data = await getProviderRequest(entId, pageNumber, pageItemCount, status, ownerId);
      let list = Array.isArray(data?.requests) ? data.requests : [];
      
      setProducts(list);
      setPagination(data?.pagination || { currentPage: 1, totalPages: 1, totalItems: 0, itemsPerPage: pageItemCount, hasNextPage: false, hasPrevPage: false });
    } catch (err) {
      setError('Error al cargar productos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts(selectedTab, page, itemsPerPage);
    // eslint-disable-next-line
  }, [selectedTab, page, itemsPerPage]);

  useEffect(() => {
    fetchProducts(selectedTab, 1, itemsPerPage);
    setPage(1);
    // eslint-disable-next-line
  }, [globalContext.currentEnterpriseId]);

  // Función para abrir popup de cancelar
  const handleCancelOrder = (product) => {
    setSelectedCancelProduct(product);
    setShowCancelPopup(true);
    setCancelReason('');
    setCancelError('');
  };
  // Función para confirmar cancelación
  const handleConfirmCancel = async () => {
    if (!cancelReason.trim()) {
      setCancelError('El motivo de cancelación es obligatorio');
      return;
    }
    
    if (cancelReason.trim().length < 8) {
      setCancelError('El motivo de cancelación debe tener al menos 8 caracteres');
      return;
    }
    
    setCancelError('');
    try {
      const entId = '5f1378dcef2f752aeda16673';
      const resp = await cancelProviderRequest(entId, selectedCancelProduct.id.toString(), cancelReason);

      if (resp) {
        setProducts(products.filter(p => p.id !== selectedCancelProduct.id));
        setSuccessMessage('¡Has cancelado correctamente la orden!');
        setSelectedProduct(null); // Cerrar modal de detalles
      } else {
        setError('No se pudo cancelar la orden.');
      }
    } catch (e) {
      setError('Error al cancelar la orden');
    }
    setShowCancelPopup(false);
    setSelectedCancelProduct(null);
    setCancelReason('');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  // Función para manejar el marcado como revisado
  const handleMarkAsReviewed = async (product) => {
    try {
      const productId = product?.id;
      console.log('Intentando marcar como revisado', { productId });
      
      if (!productId) {
        setError('Faltan datos para marcar como revisado');
        return;
      }
      
      const resp = await markProductAsReviewed(productId);
      console.log('Respuesta markProductAsReviewed', resp);
      
      if (resp && resp.success) {
        setSuccessMessage('¡Producto marcado como revisado!');
        setTimeout(() => setSuccessMessage(''), 3000);
        
        // Forzar re-render de las tarjetas para actualizar el estado visual
        setProducts([...products]);
        
        // Disparar evento para actualización en tiempo real
        window.dispatchEvent(new CustomEvent('productReviewed', {
          detail: { productId: productId }
        }));
      } else {
        setError('No se pudo marcar como revisado.');
      }
    } catch (e) {
      setError('Error al marcar como revisado');
      console.error('Error en markProductAsReviewed', e);
    }
  };

  return (
    <div style={{ background: '#f7f7f9', minHeight: '100vh', padding: 32 }}>
      {/* Mensaje de éxito tipo toast */}
      {successMessage && (
        <div style={{
          position: 'fixed',
          top: 30,
          right: 30,
          background: '#27ae60',
          color: '#fff',
          padding: '16px 32px',
          borderRadius: 10,
          boxShadow: '0 2px 12px #0002',
          fontWeight: 600,
          fontSize: 18,
          zIndex: 3000,
          transition: 'opacity 0.3s',
        }}>
          {successMessage}
        </div>
      )}
      <div style={{ maxWidth: 1300, margin: '0 auto' }}>
        <h2 style={{ fontWeight: 700, fontSize: 28, marginBottom: 24 }}>Productos del proveedor</h2>
        {/* Tabs de estado */}
        <TabsWidget
          tabs={TABS}
          selectedTab={selectedTab}
          onTabSelect={(key) => { setSelectedTab(key); setPage(1); }}
        />
        {/* Paginación y select de items por página */}
        <PaginationWidget
          page={page}
          totalPages={pagination.totalPages}
          onPageChange={setPage}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={(num) => { setItemsPerPage(num); setPage(1); }}
          totalItems={pagination.totalItems}
          onRefresh={() => { setPage(1); fetchProducts(selectedTab, 1, itemsPerPage); }}
        />
        {loading ? (
          <div style={{ textAlign: 'center', color: '#888', fontSize: 18 }}>Cargando productos...</div>
        ) : error ? (
          <div style={{ textAlign: 'center', color: '#e74c3c', fontSize: 18 }}>{error}</div>
        ) : products.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#888', fontSize: 18 }}>No hay productos en esta categoría.</div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              {products.map(product => (
                <div key={product.id} style={{ position: 'relative' }}>
                  <ProductCardWidget
                    product={product}
                    selectedTab={selectedTab}
                    selectedProduct={selectedProduct}
                    setSelectedProduct={setSelectedProduct}
                    convertBase64ToDataUrl={convertBase64ToDataUrl}
                  />
                  {/* Botón cancelar eliminado, ahora va en el modal */}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Modal de detalles de producto */}
      <ProductModalWidget
        product={selectedProduct}
        open={!!selectedProduct}
        onClose={() => setSelectedProduct(null)}
        showApproveReject={false}
        convertBase64ToDataUrl={convertBase64ToDataUrl}
        onShowAdditionalImages={(additionalImages) => {
          setSliderImages(additionalImages);
          setSliderOpen(true);
        }}
        // Props para cancelar orden
        onCancelOrder={handleCancelOrder}
        // Props para marcar como revisado
        onMarkAsReviewed={handleMarkAsReviewed}
        showReviewButton={selectedProduct}
      />
      <AdditionalImagesSliderModal images={sliderImages} open={sliderOpen} onClose={() => setSliderOpen(false)} />

      {/* Popup cancelar orden */}
      {showCancelPopup && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.25)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 3000
        }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, maxWidth: '500px', width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
            <h3 style={{ marginTop: 0 }}>Cancelar orden</h3>
            <p>Por favor, indica el motivo de la cancelación:</p>
            <textarea
              value={cancelReason}
              onChange={e => {
                setCancelReason(e.target.value);
                if (cancelError) setCancelError('');
              }}
              rows={4}
              style={{ 
                width: '100%', 
                borderRadius: 6, 
                border: cancelError ? '1px solid #e74c3c' : '1px solid #ccc', 
                padding: 8, 
                marginBottom: cancelError ? 8 : 16 
              }}
              placeholder="Motivo de la cancelación..."
            />
            {cancelError && (
              <div style={{ 
                color: '#e74c3c', 
                fontSize: 14, 
                marginBottom: 16,
                fontWeight: 500
              }}>
                {cancelError}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => { setShowCancelPopup(false); setSelectedProduct(null); setCancelError(''); }} style={{ padding: '8px 18px', borderRadius: 6, border: 'none', background: '#eee', cursor: 'pointer' }}>Cancelar</button>
              <button onClick={handleConfirmCancel} style={{ padding: '8px 18px', borderRadius: 6, border: 'none', background: '#e74c3c', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>Confirmar cancelación</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsSentsClient; 